import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Brain, ChevronRight, BookOpen, FileText, FileType, File, Folder } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProjectFile, ProjectPlaybook } from '../../services/Project';
import { useI18n } from '../../i18n';

type MenuCategory = 'playbooks' | 'files' | 'knowledge' | null;

interface MentionMenuProps {
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number };
  files: ProjectFile[];
  playbooks: ProjectPlaybook[];
  onSelectFile: (fileId: string) => void;
  onSelectPlaybook: (playbookId: string) => void;
  onSelectKnowledge?: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  category?: MenuCategory;
  disabled?: boolean;
  disabledText?: string;
}

const getFileIcon = (mimeType: string) => {
  if (mimeType.includes('pdf')) {
    return <FileText className="h-4 w-4 text-red-500" />;
  }
  if (mimeType.includes('word') || mimeType.includes('docx')) {
    return <FileType className="h-4 w-4 text-blue-500" />;
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return <File className="h-4 w-4 text-green-500" />;
  }
  return <Folder className="h-4 w-4 text-muted-foreground" />;
};

export const MentionMenu: React.FC<MentionMenuProps> = ({
  isOpen,
  onClose,
  position,
  files,
  playbooks,
  onSelectFile,
  onSelectPlaybook,
}) => {
  const { t } = useI18n();
  const mm = t.qagentChat.mentionMenu;
  const [expandedCategory, setExpandedCategory] = useState<MenuCategory>(null);
  const [mainSelectedIndex, setMainSelectedIndex] = useState(0);
  const [subSelectedIndex, setSubSelectedIndex] = useState(0);
  const [focusOnSub, setFocusOnSub] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Reset state when menu opens - auto-expand the first non-disabled category
  useEffect(() => {
    if (isOpen) {
      const firstEnabled = mainMenuItems.find(item => !item.disabled);
      setExpandedCategory(firstEnabled?.category ?? null);
      setMainSelectedIndex(firstEnabled ? mainMenuItems.indexOf(firstEnabled) : 0);
      setSubSelectedIndex(0);
      setFocusOnSub(false);
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const mainMenuItems: MenuItem[] = [
    {
      id: 'playbooks',
      label: mm.playbooks,
      icon: <BookOpen className="h-4 w-4 text-brand" />,
      category: 'playbooks',
      disabled: playbooks.length === 0,
      disabledText: mm.noPlaybooks,
    },
    {
      id: 'files',
      label: mm.files,
      icon: <FileText className="h-4 w-4 text-brand" />,
      category: 'files',
      disabled: files.filter(f => f.status === 'ready').length === 0,
      disabledText: mm.noFiles,
    },
    {
      id: 'knowledge',
      label: mm.knowledge,
      icon: <Brain className="h-4 w-4 text-brand" />,
      category: 'knowledge',
      disabled: true,
      disabledText: mm.soon,
    },
  ];

  const playbookItems: MenuItem[] = playbooks.map((pb) => ({
    id: pb.playbookId,
    label: pb.playbookName,
    icon: <BookOpen className="h-4 w-4 text-muted-foreground" />,
  }));

  const fileItems: MenuItem[] = files
    .filter((f) => f.status === 'ready')
    .map((f) => ({
      id: f.id,
      label: f.name,
      icon: getFileIcon(f.mimeType),
    }));

  const getSubItems = (): MenuItem[] => {
    switch (expandedCategory) {
      case 'playbooks':
        return playbookItems;
      case 'files':
        return fileItems;
      default:
        return [];
    }
  };

  const subItems = getSubItems();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (focusOnSub && subItems.length > 0) {
            setSubSelectedIndex((prev) => (prev + 1) % subItems.length);
          } else {
            setMainSelectedIndex((prev) => (prev + 1) % mainMenuItems.length);
            // Auto-expand on navigation
            const newItem = mainMenuItems[(mainSelectedIndex + 1) % mainMenuItems.length];
            if (newItem.category && !newItem.disabled) {
              setExpandedCategory(newItem.category);
              setSubSelectedIndex(0);
            }
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (focusOnSub && subItems.length > 0) {
            setSubSelectedIndex((prev) => (prev - 1 + subItems.length) % subItems.length);
          } else {
            const newIndex = (mainSelectedIndex - 1 + mainMenuItems.length) % mainMenuItems.length;
            setMainSelectedIndex(newIndex);
            // Auto-expand on navigation
            const newItem = mainMenuItems[newIndex];
            if (newItem.category && !newItem.disabled) {
              setExpandedCategory(newItem.category);
              setSubSelectedIndex(0);
            }
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (!focusOnSub && expandedCategory && subItems.length > 0) {
            setFocusOnSub(true);
          }
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (focusOnSub) {
            setFocusOnSub(false);
          }
          break;
        case 'Enter':
          event.preventDefault();
          if (focusOnSub && subItems.length > 0) {
            handleSubSelect(subItems[subSelectedIndex]);
          } else if (expandedCategory && subItems.length > 0) {
            setFocusOnSub(true);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    },
    [isOpen, mainMenuItems, mainSelectedIndex, subItems, subSelectedIndex, focusOnSub, expandedCategory, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleMainHover = (item: MenuItem, index: number) => {
    setMainSelectedIndex(index);
    setFocusOnSub(false);
    if (item.category && !item.disabled) {
      setExpandedCategory(item.category);
      setSubSelectedIndex(0);
    } else {
      setExpandedCategory(null);
    }
  };

  const handleSubSelect = (item: MenuItem) => {
    if (expandedCategory === 'playbooks') {
      onSelectPlaybook(item.id);
      onClose();
    } else if (expandedCategory === 'files') {
      onSelectFile(item.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="absolute z-50 left-0 right-0"
      style={{
        bottom: '100%',
      }}
    >
      {/* Single unified container for all panels */}
      <div className="bg-popover border border-border border-b-0 rounded-t-xl overflow-hidden">
        {/* Shared header row spanning all columns */}
        <div className="flex border-b bg-muted/30">
          <div className="px-3 py-1.5 min-w-[180px]">
            <span className="text-sm font-medium">{mm.title}</span>
          </div>
          {expandedCategory && expandedCategory !== 'knowledge' && (
            <div className="px-3 py-1.5 min-w-[200px] flex items-center gap-2">
              {expandedCategory === 'playbooks' && (
                <>
                  <BookOpen className="h-4 w-4 text-brand" />
                  <span className="text-sm font-medium">{mm.playbooks}</span>
                </>
              )}
              {expandedCategory === 'files' && (
                <>
                  <FileText className="h-4 w-4 text-brand" />
                  <span className="text-sm font-medium">{mm.files}</span>
                </>
              )}
            </div>
          )}
          {expandedCategory === 'knowledge' && (
            <div className="px-3 py-1.5 min-w-[200px] flex items-center gap-2">
              <Brain className="h-4 w-4 text-brand" />
              <span className="text-sm font-medium">{mm.knowledge}</span>
            </div>
          )}
        </div>

        {/* Body columns */}
        <div className="flex">
          {/* Left column - categories */}
          <div className="min-w-[180px] flex-shrink-0 py-1">
            {mainMenuItems.map((item, index) => (
              <button
                key={item.id}
                onMouseEnter={() => handleMainHover(item, index)}
                onClick={() => {
                  if (!item.disabled && item.category) {
                    setExpandedCategory(item.category);
                    setFocusOnSub(true);
                  }
                }}
                disabled={item.disabled}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                  "hover:bg-muted focus:outline-none",
                  mainSelectedIndex === index && !focusOnSub && "bg-muted",
                  mainSelectedIndex === index && expandedCategory === item.category && "bg-muted",
                  item.disabled && "opacity-50 cursor-not-allowed"
                )}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
                {item.disabled ? (
                  <span className="text-xs text-muted-foreground">{item.disabledText || mm.unavailable}</span>
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            ))}
          </div>

          {/* Right column - expanded sub-items */}
          {expandedCategory && expandedCategory !== 'knowledge' && (
            <div className="self-stretch flex-1 py-1 max-h-[250px] overflow-y-auto">
              {subItems.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  {mm.noItems}
                </div>
              ) : (
                subItems.map((item, index) => (
                  <button
                    key={item.id}
                    onMouseEnter={() => {
                      setSubSelectedIndex(index);
                      setFocusOnSub(true);
                    }}
                    onClick={() => handleSubSelect(item)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                      "hover:bg-muted focus:outline-none",
                      focusOnSub && subSelectedIndex === index && "bg-muted"
                    )}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    <span className="flex-1 truncate">{item.label}</span>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Knowledge Coming Soon column */}
          {expandedCategory === 'knowledge' && (
            <div className="self-stretch min-w-[200px] flex-1">
              <div className="px-4 py-6 text-center">
                <Brain className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-sm font-medium text-muted-foreground">{mm.comingSoon}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {mm.knowledgeHint}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MentionMenu;
